import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

type Props = {
  onToggleMobileSidebar: () => void
  /** Lookup label → path para mostrar la sección activa (breadcrumb). */
  pathToLabel: Record<string, string>
  /** Ruta base del perfil (/perfil para usuario, /admin/perfil para admin) */
  profilePath: string
}

/**
 * Topbar (§5.6 SRS) — fija en la parte superior:
 *   - Botón hamburguesa para colapsar/expandir el sidebar.
 *   - Breadcrumb de la sección activa.
 *   - Avatar del usuario con dropdown rápido a Perfil + Cerrar Sesión.
 */
export function Topbar({ onToggleMobileSidebar, pathToLabel, profilePath }: Props) {
  const { state, logout } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)

  // Match más específico primero (path más largo).
  const matchedPath = Object.keys(pathToLabel)
    .sort((a, b) => b.length - a.length)
    .find((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))

  const sectionLabel = matchedPath ? pathToLabel[matchedPath] : ''

  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar__hamburger topbar__hamburger--mobile"
        onClick={onToggleMobileSidebar}
        aria-label="Abrir menú lateral"
      >
        <span aria-hidden="true">☰</span>
      </button>

      <div className="topbar__breadcrumb" aria-live="polite">
        {sectionLabel}
      </div>

      <div className="topbar__right">
        {state.status === 'authenticated' && (
          <div className={`topbar__avatar-wrap ${open ? 'open' : ''}`}>
            <button
              type="button"
              className="topbar__avatar"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-haspopup="menu"
              title={`${state.user.first_name} ${state.user.last_name_paternal}`}
            >
              {(state.user.first_name[0] ?? '') + (state.user.last_name_paternal[0] ?? '')}
            </button>
            {open && (
              <>
                <div className="topbar__avatar-backdrop" onClick={() => setOpen(false)} />
                <div role="menu" className="topbar__menu">
                  <div className="topbar__menu-header">
                    <strong>{state.user.first_name} {state.user.last_name_paternal}</strong>
                    <small>{state.user.email}</small>
                  </div>
                  <Link to={profilePath} className="topbar__menu-item" onClick={() => setOpen(false)} role="menuitem">
                    Perfil
                  </Link>
                  <button
                    type="button"
                    className="topbar__menu-item"
                    onClick={() => {
                      setOpen(false)
                      logout()
                    }}
                    role="menuitem"
                  >
                    Cerrar sesión
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
